import { Dispatch, SetStateAction } from 'react';
import Bell from '@monofront/design-system/visuals/Icons/Old/Bell';
import Button from '@monofront/design-system/components/Button';
import { useAuthentication } from '@context/businessContainer';
import { toast } from 'react-toastify';
import { useNotifyMe } from '@monofront/business/hooks';
import ModalNotifyMe from '@components/ModalNotifyMe';
import { getIsAntiWaste } from '@monofront/business/utils';
import { useTranslations } from 'next-intl';

interface INotifyMeButtonProps {
  productTitle: string;
  sku: string;
  inventoryAvailable: any;
  productTags: string[];
  isNotifyMeModalOpen: boolean;
  setIsNotifyMeModalOpen: Dispatch<SetStateAction<boolean>>;
  hasIcon?: boolean;
}

const NotifyMeButton: React.FC<INotifyMeButtonProps> = ({
  productTitle,
  sku,
  productTags,
  inventoryAvailable,
  isNotifyMeModalOpen,
  setIsNotifyMeModalOpen,
  hasIcon = false,
}) => {
  const { isAuthenticated, customer } = useAuthentication();
  const { loading, notifyMe } = useNotifyMe();
  const isAntiWaste = getIsAntiWaste(productTags);
  const tCatalog = useTranslations('Catalog');

  const handleNotifyMe = async (): Promise<void> => {
    await notifyMe({
      email: customer!.email,
      sku,
      successCallback: (messageKey) => {
        toast(tCatalog(messageKey));
      },
      errorCallback: (errorMessageKey) => {
        toast(tCatalog(errorMessageKey), {
          progressClassName: 'toast-error',
        });
      },
    });
  };

  if (inventoryAvailable > 0 || isAntiWaste) {
    return null;
  }

  if (!isAuthenticated || !customer?.email) {
    return (
      <>
        <ModalNotifyMe
          title={productTitle}
          isOpen={isNotifyMeModalOpen}
          externalToggle={() => setIsNotifyMeModalOpen(!isNotifyMeModalOpen)}
          sku={sku}
        />
        <Button
          hasMinWidth={false}
          variant="secondary"
          onClick={() => setIsNotifyMeModalOpen(true)}
          isLoading={loading}
          dataTestId="NotifyMeButton-Button-open"
        >
          {hasIcon && <Bell />}
          {tCatalog('notifyMeV2')}
        </Button>
      </>
    );
  }

  return (
    <Button
      hasMinWidth={false}
      variant="secondary"
      onClick={() => handleNotifyMe()}
      isLoading={loading}
      dataTestId="NotifyMeButton-Button-submit"
    >
      {hasIcon && <Bell />}
      {tCatalog('notifyMeV2')}
    </Button>
  );
};

export default NotifyMeButton;
